import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, TemplateRef } from '@angular/core';

import { Position as Position, TooltipOptions } from './tooltip.model';

@Component({
  selector: 'cp-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TooltipComponent implements AfterViewInit {
  @Input()
  content: TemplateRef<any>;

  @Input()
  parent: ElementRef;

  @Input()
  options: TooltipOptions;

  @Input()
  data: any;

  constructor(public elementRef: ElementRef) {
  }

  ngAfterViewInit() {
    this.positionSelf();
  }

  private positionSelf() {
    const $element = this.elementRef.nativeElement as HTMLElement;
    const position = this.positionElements(this.parent.nativeElement, this.elementRef.nativeElement,
                                           this.options.position, this.options.margin);
    $element.className = `${this.options.position} ${this.options.popupClass}`;
    this.setPosition($element, position);
  }

  private positionElements(host: HTMLElement, target: HTMLElement,
                           position: Position, margin: number = 0): { top: number, left: number } {
    let top: number;
    let left: number;

    const hostRect = host.getBoundingClientRect();
    const hostPosition = { top: hostRect.top + window.pageYOffset, left: hostRect.left + window.pageXOffset }
    switch (position) {
      case 'top':
        top = hostPosition.top - this.getInnerHeight(target) - margin;
        left = this.calculateHorizontalCenter(hostPosition, host, target);
        break;
      case 'bottom':
        top = hostPosition.top + this.getHeight(host) + margin;
        left = this.calculateHorizontalCenter(hostPosition, host, target);
        break;
      case 'left':
        top = this.calculateVerticalCenter(hostPosition, host, target);
        left = hostPosition.left - this.getInnerWidth(target) - margin;
        break;
      case 'right':
        top = this.calculateVerticalCenter(hostPosition, host, target);
        left = hostPosition.left + this.getWidth(host) + margin;
        break;
      default:
        throw new Error('Unexpected position');
    }

    return { top, left };
  }

  private calculateHorizontalCenter(hostPosition: { left: number, top: number }, host: HTMLElement, target: HTMLElement) {
    return hostPosition.left + (this.getWidth(host) / 2) - (this.getInnerWidth(target) / 2);
  }

  private calculateVerticalCenter(hostPosition: { left: number, top: number }, host: HTMLElement, target: HTMLElement) {
    return hostPosition.top + (this.getHeight(host) / 2) - (this.getInnerHeight(target) / 2);
  }

  private setPosition(element: HTMLElement, position: { top: number, left: number }) {
    element.style.top = `${position.top}px`;
    element.style.left = `${position.left}px`;
  }

  private getInnerHeight(element: HTMLElement) {
    const style = getComputedStyle(element);
    return element.offsetHeight - parseInt(style.borderTopWidth!, 10) - parseInt(style.borderBottomWidth!, 10);
  }

  private getHeight(element: HTMLElement) {
    const style = getComputedStyle(element);
    return this.getInnerHeight(element) - parseInt(style.paddingTop!, 10) - parseInt(style.paddingBottom!, 10);
  }

  private getInnerWidth(element: HTMLElement) {
    const style = getComputedStyle(element);
    return element.offsetWidth - parseInt(style.borderLeftWidth!, 10) - parseInt(style.borderRightWidth!, 10);
  }

  private getWidth(element: HTMLElement) {
    const style = getComputedStyle(element);
    return this.getInnerWidth(element) - parseInt(style.paddingLeft!, 10) - parseInt(style.paddingRight!, 10);
  }
}
